import {
  fetchStockScore as apiStockScore,
  stockSearch as apiStockSearch,
} from './finnhub';

// ─── Helpers ────────────────────────────────────────────────────────────────

function safeNum(val, fallback = 0) {
  const n = Number(val);
  return isFinite(n) ? n : fallback;
}

/** Extract a named concept from a reported period's financials array */
function getConcept(report, concept) {
  if (!report?.report) return null;
  const sections = ['ic', 'bs', 'cf'];
  for (const sec of sections) {
    const arr = report.report[sec];
    if (!Array.isArray(arr)) continue;
    const item = arr.find(
      (r) => r.concept?.toLowerCase() === concept.toLowerCase()
    );
    if (item?.value !== undefined) return safeNum(item.value);
  }
  return null;
}

/** Average an array of quarterly reports into a "year" value */
function avgQuarters(reports, concept) {
  const vals = reports
    .map((r) => getConcept(r, concept))
    .filter((v) => v !== null && v !== 0);
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// ─── Score Calculator ────────────────────────────────────────────────────────

export function calculateMetrics({ price, eps_ttm, eps_4y_ago, quarters }) {
  const sharePrice = safeNum(price);
  const eps_4y = eps_4y_ago;

  // ── 1. Fair value (Tykr: EPS growth from 4Y → annualized CAGR, swim lane, FV) ─
  if (eps_ttm == null || !(eps_ttm > 0) || !isFinite(eps_ttm)) {
    throw new Error('INSUFFICIENT_EPS_TTM');
  }
  if (eps_4y == null || eps_4y === 0 || !(eps_4y > 0) || !isFinite(eps_4y)) {
    throw new Error('INSUFFICIENT_EPS_4Y');
  }

  const totalEpsGrowth = (eps_ttm - eps_4y) / Math.abs(eps_4y);
  let annualizedGrowth = Math.pow(1 + totalEpsGrowth, 1 / 4) - 1;
  if (!isFinite(annualizedGrowth)) {
    throw new Error('INSUFFICIENT_GROWTH');
  }
  if (annualizedGrowth >= 0.35) annualizedGrowth = 0.35;
  if (annualizedGrowth <= 0.15) annualizedGrowth = 0.15;

  const fairValue = (sharePrice * Math.pow(1 + annualizedGrowth, 10)) / 4;
  if (!(fairValue > 0) || !isFinite(fairValue)) {
    throw new Error('INSUFFICIENT_FAIR_VALUE');
  }

  // ── 2. Margin of Safety ───────────────────────────────────────────────────
  const mos = 1 - sharePrice / fairValue;

  // ── 3. Score (max raw = 94) ───────────────────────────────────────────────
  let rawScore = 0;

  // ROIC = EBITDA / (Debt + Equity)  — max 30 pts
  const roicHistory = quarters.slice(0, 5).map((q) => {
    const ebitda = safeNum(q.ebitda);
    const debt = safeNum(q.debt);
    const equity = safeNum(q.equity);
    const denom = debt + equity;
    return denom !== 0 ? (ebitda / denom) * 100 : null;
  });

  let roicScore = 0;
  const validRoic = roicHistory.filter((r) => r !== null);
  if (validRoic.length >= 5) {
    if (validRoic.slice(0, 5).every((r) => r >= 10)) roicScore += 25;
    if (validRoic[0] > validRoic[4]) roicScore += 5;
  }
  rawScore += Math.min(roicScore, 30);

  // Growth helper (max 16 pts each)
  const growthScore = (getVal) => {
    const g = (a, b) => {
      const av = safeNum(a);
      const bv = safeNum(b);
      return bv !== 0 ? ((av - bv) / Math.abs(bv)) * 100 : null;
    };

    const y = [
      avgQuarters(quarters.slice(0, 4), getVal),
      avgQuarters(quarters.slice(4, 8), getVal),
      avgQuarters(quarters.slice(8, 12), getVal),
      avgQuarters(quarters.slice(12, 16), getVal),
    ];

    let s = 0;
    if (y[0] !== null && y[1] !== null && g(y[0], y[1]) >= 10) s += 4;
    if (y[0] !== null && y[2] !== null && g(y[0], y[2]) >= 10) s += 4;
    if (y[0] !== null && y[3] !== null && g(y[0], y[3]) >= 10) s += 4;
    const gRate01 = g(y[0], y[1]);
    const gRate12 = g(y[1], y[2]);
    if (gRate01 !== null && gRate12 !== null && gRate01 > gRate12) s += 4;
    return Math.min(s, 16);
  };

  rawScore += growthScore('us-gaap/StockholdersEquity');
  rawScore += growthScore('us-gaap/EarningsPerShareBasic');
  rawScore += growthScore('us-gaap/Revenues');
  rawScore += growthScore('us-gaap/NetCashProvidedByUsedInOperatingActivities');

  const finalScore = Math.round((rawScore / 94) * 100);

  // ── 4. Resumen (Tykr-aligned MOS bands) ────────────────────────────────────
  let status = 'Vigilar';
  if (mos <= 0) status = 'Sobrevalorado';
  else if (mos >= 0.5 && finalScore >= 50) status = 'En Oferta';

  return { fairValue, mos, score: finalScore, status };
}

/**
 * Core scoring pipeline from raw Finnhub payloads (used client + Netlify).
 */
/**
 * @param {object} [scoringOptions]
 * @param {number|null|undefined} [scoringOptions.eps_4y_ago] When set (including `null`), uses this instead of metric.epsBasicExclExtraItems4Y only. Server resolves a real-data waterfall before calling.
 */
export function buildStockResult(
  symbol,
  displayName,
  quoteData,
  metricsData,
  financialsData,
  scoringOptions
) {
  if (!quoteData || !quoteData.c || quoteData.c === 0) {
    throw new Error(`No se encontraron datos para "${symbol}"`);
  }

  const price = safeNum(quoteData.c);
  const m = metricsData?.metric || {};

  const epsRaw = m.epsBasicExclExtraItemsTTM ?? m.epsTTM;
  const eps_ttm = safeNum(epsRaw);
  if (!(eps_ttm > 0)) {
    throw new Error(`INSUFFICIENT_EPS:${symbol}`);
  }

  const reported = financialsData?.data ?? [];
  let eps_4y_ago;
  if (scoringOptions && Object.prototype.hasOwnProperty.call(scoringOptions, 'eps_4y_ago')) {
    const injected = scoringOptions.eps_4y_ago;
    const n = Number(injected);
    if (injected == null || !isFinite(n) || !(n > 0)) {
      throw new Error(`INSUFFICIENT_EPS_4Y:${symbol}`);
    }
    eps_4y_ago = n;
  } else {
    const e4 = safeNum(m.epsBasicExclExtraItems4Y);
    if (!(e4 > 0)) {
      throw new Error(`INSUFFICIENT_EPS_4Y:${symbol}`);
    }
    eps_4y_ago = e4;
  }

  const quarters = reported.slice(0, 16).map((period) => {
    const get = (concept) => getConcept(period, concept);
    return {
      year: period.year,
      quarter: period.quarter,
      ebitda:
        get('us-gaap/OperatingIncomeLoss') ??
        get('us-gaap/NetIncomeLoss') ??
        0,
      debt:
        get('us-gaap/LongTermDebt') ??
        get('us-gaap/LongTermDebtNoncurrent') ??
        0,
      equity:
        get('us-gaap/StockholdersEquity') ??
        get('us-gaap/StockholdersEquityAttributableToParent') ??
        0,
      report: period,
    };
  });

  while (quarters.length < 16) {
    quarters.push({ ebitda: 0, debt: 0, equity: 0, report: null });
  }

  const metrics = calculateMetrics({ price, eps_ttm, eps_4y_ago, quarters });

  return {
    symbol: symbol.toUpperCase(),
    name: displayName || symbol.toUpperCase(),
    price,
    eps_ttm,
    eps_4y_ago,
    quarters,
    ...metrics,
  };
}

// ─── Netlify API client (no secrets in browser) ───────────────────────────────

function enrichClientPayload(base) {
  const summary = `Estado: ${base.status}. MOS ${(base.mos * 100).toFixed(1)}%. Puntaje ${base.score}/100.`;
  return {
    ...base,
    summary,
    badge: base.status,
  };
}

function isNoDataScore(raw) {
  if (!raw || typeof raw !== 'object') return true;
  if (raw.error != null) return true;
  if (raw.badge === 'gray') return true;
  if (raw.score === null) return true;
  return false;
}

export async function fetchStockData(symbol) {
  const raw = await apiStockScore(symbol);
  if (isNoDataScore(raw)) {
    return {
      noData: true,
      symbol: String(raw.ticker || raw.symbol || symbol).toUpperCase(),
      name: raw.name ?? raw.ticker ?? symbol,
      message: raw.error || 'Datos no disponibles para este activo',
      summary: raw.summary ?? 'Sin datos',
      status: 'Sin datos',
      badge: 'gray',
    };
  }
  const base = {
    symbol: raw.symbol,
    name: raw.name ?? symbol,
    price: raw.price,
    eps_ttm: raw.eps_ttm,
    eps_4y_ago: raw.eps_4y_ago,
    quarters: raw.quarters ?? [],
    fairValue: raw.fairValue,
    mos: raw.mos,
    score: raw.score,
    status: raw.status ?? raw.badge,
  };
  return base;
}

// ─── Search Stocks ────────────────────────────────────────────────────────────

export async function searchStocks(query) {
  if (!query || query.length < 1) return [];

  const candidates = (await apiStockSearch(query))
    .filter((r) => r.type === 'Common Stock' || r.type === '')
    .slice(0, 5);

  if (!candidates.length) throw new Error(`Sin resultados para "${query}"`);

  const results = await Promise.all(
    candidates.map(async (c) => {
      try {
        const raw = await apiStockScore(c.ticker);
        if (isNoDataScore(raw)) {
          return {
            symbol: String(raw.ticker || c.ticker).toUpperCase(),
            name: c.name || c.ticker,
            price: raw.price ?? null,
            eps_ttm: null,
            eps_4y_ago: null,
            quarters: [],
            fairValue: null,
            mos: null,
            score: null,
            status: 'Sin datos',
            summary: raw.summary || 'Sin datos',
            badge: 'gray',
            errorMessage: raw.error,
          };
        }
        const base = {
          symbol: raw.symbol,
          name: c.name || raw.symbol,
          price: raw.price,
          eps_ttm: raw.eps_ttm,
          eps_4y_ago: raw.eps_4y_ago,
          quarters: raw.quarters ?? [],
          fairValue: raw.fairValue,
          mos: raw.mos,
          score: raw.score,
          status: raw.status ?? raw.badge,
        };
        return enrichClientPayload(base);
      } catch (e) {
        return {
          symbol: c.ticker.toUpperCase(),
          name: c.name || c.ticker,
          price: null,
          score: null,
          mos: null,
          status: 'Sin datos',
          summary: 'Sin datos',
          badge: 'gray',
          errorMessage: e.message || 'Error al cargar',
        };
      }
    })
  );

  return results;
}
